import { Outlet } from '@tanstack/react-router';
import { ChartLine, Link2, Send, Settings, SquarePen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';

import { useStore } from '@/shared/store/store-context';

import { NetworkLayout, type NetworkNavGroup } from '../components/network-layout';
import { NetworkStatusBadge } from '../components/network-status-badge';
import { useSocialConnections } from '../hooks/use-social-connections';
import { accountHandle, connectionState, findConnection } from '../schemas/channel-state';

/** Social › Pinterest: the secondary sidebar around every Pinterest page. */
export function PinterestLayout() {
  const { t } = useTranslation('social');
  const store = useStore();
  const { can } = usePermissions();
  const connections = useSocialConnections(store.id);
  const connection = connections.data
    ? findConnection(connections.data.items, 'pinterest')
    : undefined;
  const state = connectionState(connection);
  const params = { storeId: store.id };

  const groups: NetworkNavGroup[] = [
    {
      key: 'publish',
      label: t('nav.groups.publish'),
      items: [
        {
          key: 'posts',
          label: t('nav.posts'),
          icon: Send,
          link: { to: '/s/$storeId/social/pinterest/posts', params },
          tour: 'social.pinterest.nav.posts',
        },
        ...(can('social.write')
          ? [
              {
                key: 'create',
                label: t('nav.create'),
                icon: SquarePen,
                link: { to: '/s/$storeId/social/pinterest/create' as const, params },
                tour: 'social.pinterest.nav.create',
              },
            ]
          : []),
      ],
    },
    {
      key: 'insights',
      label: t('nav.groups.insights'),
      items: [
        {
          key: 'analytics',
          label: t('nav.analytics'),
          icon: ChartLine,
          link: { to: '/s/$storeId/social/pinterest/analytics', params },
          tour: 'social.pinterest.nav.analytics',
        },
      ],
    },
    {
      key: 'setup',
      label: t('nav.groups.setup'),
      items: [
        {
          key: 'connection',
          label: t('nav.connection'),
          icon: Link2,
          link: { to: '/s/$storeId/social/pinterest/connection', params },
          tour: 'social.pinterest.nav.connection',
        },
        {
          key: 'settings',
          label: t('nav.settings'),
          icon: Settings,
          link: { to: '/s/$storeId/social/pinterest/settings', params },
          tour: 'social.pinterest.nav.settings',
        },
      ],
    },
  ];

  const subtitle = connections.isPending
    ? null
    : connection && state !== 'disconnected'
      ? accountHandle(connection)
      : t('hub.notConnected');

  return (
    <NetworkLayout
      storeId={store.id}
      network="pinterest"
      name={t('pinterest.name')}
      subtitle={subtitle}
      badge={
        connections.isPending || state === 'connected' ? null : <NetworkStatusBadge state={state} />
      }
      groups={groups}
    >
      <Outlet />
    </NetworkLayout>
  );
}
